import React, { useMemo, useState } from "react";

import { redo, undo } from "@codemirror/commands";
import { searchPanelOpen } from "@codemirror/search";
import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import ManuscriptFigureDropdown from "components/organisms/manuscript/overlay/ManuscriptFigureDropdown";
import ManuscriptSymbolDropdown from "components/organisms/manuscript/overlay/ManuscriptSymbolDropdown";
import ManuscriptTableDropdown from "components/organisms/manuscript/overlay/ManuscriptTableDropdown";
import {
    ancestorListType,
    toolbarCommands,
    withinFormattingCommand,
} from "components/organisms/manuscript/adapter/visual";
import { findCurrentSectionHeadingLevel, setSectionHeadingLevel } from "components/organisms/manuscript/adapter/visual";

const ToolbarButtonGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;

    &.overflow-hidden {
        display: none;
    }
`;

const ToolbarDivider = styled.div`
    width: 1px;
    height: 18px;
    background: #d8e1ec;
`;

const ModeToggleGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding-right: 2px;
    border-right: 1px solid #d8e1ec;
`;

const SectionSelect = styled.select`
    height: 24px;
    min-width: 118px;
    padding: 0 26px 0 8px;
    border: 1px solid #d9e2ec;
    border-radius: 4px;
    background: #ffffff;
    color: #42576b;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const DEFAULT_ICON_COLOR = "#42576b";
const DIFF_ICON_COLOR = "red";

const buttonParity = {
    visualMode: true,
    codeMode: true,
    undo: true,
    redo: true,
    bold: true,
    italic: true,
    href: true,
    ref: true,
    cite: true,
    bulletList: true,
    numberedList: true,
    table: true,
    figure: true,
    symbol: true,
    inlineMath: true,
    displayMath: true,
    search: true,
};

const iconColor = (matchesOverleaf) => (matchesOverleaf ? DEFAULT_ICON_COLOR : DIFF_ICON_COLOR);

function ManuscriptEditorToolbar({
    Button,
    editorRef,
    editorContent,
    selection,
    selectedNode,
    isTextDocument,
    isVisualMode,
    canShowVisualToggle,
    onEditorModeChange,
    runEditorCommand,
    onOpenInsertFigureModal,
    setToast,
    overflowed,
}) {
    const [activeToolbarDropdown, setActiveToolbarDropdown] = useState(null);
    const isOverflowMenu = Boolean(overflowed);
    const showGroup = (group) => !overflowed || overflowed.has(group);

    const sectionLevelOptions = useMemo(
        () => [
            { value: "text", label: "본문" },
            { value: "section", label: "섹션" },
            { value: "subsection", label: "소제목" },
            { value: "subsubsection", label: "하위 소제목" },
            { value: "paragraph", label: "문단 제목" },
            { value: "subparagraph", label: "하위 문단 제목" },
        ],
        [],
    );

    const currentSectionLevel = useMemo(() => {
        const editorView = editorRef?.current?.view;
        if (!editorView || !isTextDocument) {
            return "text";
        }

        return findCurrentSectionHeadingLevel(editorView.state)?.level || "text";
    }, [editorContent, editorRef, isTextDocument, selection]);

    const activeFormatting = useMemo(() => {
        const editorView = editorRef?.current?.view;
        if (!editorView || !isTextDocument) {
            return {
                bold: false,
                italic: false,
                bulletList: false,
                numberedList: false,
                search: false,
            };
        }

        const isActive = withinFormattingCommand(editorView.state);
        const listType = ancestorListType(editorView.state);
        return {
            bold: isActive("\\textbf"),
            italic: isActive("\\textit"),
            bulletList: listType === "itemize",
            numberedList: listType === "enumerate",
            search: searchPanelOpen(editorView.state),
        };
    }, [editorContent, editorRef, isTextDocument, selection]);

    const handleSectionLevelChange = (event) => {
        const nextLevel = event.target.value;
        const editorView = editorRef?.current?.view;
        if (!editorView || !isTextDocument) return;

        editorView.focus();
        setSectionHeadingLevel(editorView, nextLevel);
    };

    return (
        <>
            {!isOverflowMenu ? (
                <>
                    <ToolbarButtonGroup aria-label="에디터 모드 전환">
                        <ModeToggleGroup>
                            <Button
                                type="button"
                                $active={isVisualMode}
                                disabled={!canShowVisualToggle}
                                onClick={() => {
                                    if (!canShowVisualToggle) {
                                        setToast("이 문서는 visual editor를 지원하지 않습니다.", "info");
                                        return;
                                    }
                                    onEditorModeChange?.("visual");
                                }}
                            >
                                <Icon name={"textStyle"} size={"12px"} color={iconColor(buttonParity.visualMode)} />
                                Visual
                            </Button>
                            <Button type="button" $active={!isVisualMode} onClick={() => onEditorModeChange?.("code")}>
                                <Icon name={"textBlock"} size={"12px"} color={iconColor(buttonParity.codeMode)} />
                                LaTeX
                            </Button>
                        </ModeToggleGroup>
                    </ToolbarButtonGroup>
                    <ToolbarButtonGroup aria-label="실행 취소 및 다시 실행">
                        <Tooltip message="실행 취소">
                            <Button type="button" onClick={() => runEditorCommand(undo)} disabled={!selectedNode}>
                                <Icon name={"undo"} size={"14px"} color={iconColor(buttonParity.undo)} />
                            </Button>
                        </Tooltip>
                        <Tooltip message="다시 실행">
                            <Button type="button" onClick={() => runEditorCommand(redo)} disabled={!selectedNode}>
                                <Icon name={"redo"} size={"14px"} color={iconColor(buttonParity.redo)} />
                            </Button>
                        </Tooltip>
                    </ToolbarButtonGroup>
                </>
            ) : null}
            {showGroup("group-section") && (
                <ToolbarButtonGroup data-overflow="group-section" aria-label="제목 레벨">
                    <ToolbarDivider />
                    <Tooltip message="제목 레벨">
                        <SectionSelect
                            value={currentSectionLevel}
                            onChange={handleSectionLevelChange}
                            disabled={!isTextDocument}
                            aria-label="제목 레벨 선택"
                        >
                            {sectionLevelOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </SectionSelect>
                    </Tooltip>
                </ToolbarButtonGroup>
            )}
            {showGroup("group-format") && (
                <ToolbarButtonGroup data-overflow="group-format" aria-label="텍스트 스타일">
                    <Tooltip message="굵게">
                        <Button
                            type="button"
                            $active={activeFormatting.bold}
                            onClick={() => runEditorCommand(toolbarCommands.toggleBold)}
                            disabled={!isTextDocument}
                        >
                            <Icon name={"textBold"} size={"10px"} color={iconColor(buttonParity.bold)} />
                        </Button>
                    </Tooltip>
                    <Tooltip message="기울임">
                        <Button
                            type="button"
                            $active={activeFormatting.italic}
                            onClick={() => runEditorCommand(toolbarCommands.toggleItalic)}
                            disabled={!isTextDocument}
                        >
                            <Icon name={"textItalic"} size={"10px"} color={iconColor(buttonParity.italic)} />
                        </Button>
                    </Tooltip>
                </ToolbarButtonGroup>
            )}
            {showGroup("group-misc") && (
                <ToolbarButtonGroup data-overflow="group-misc" aria-label="삽입 및 참조">
                    <ToolbarDivider />
                    <Tooltip message="외부 링크">
                        <Button
                            type="button"
                            onClick={() => runEditorCommand(toolbarCommands.wrapInHref)}
                            disabled={!isTextDocument}
                        >
                            <Icon name={"link"} size={"10px"} color={iconColor(buttonParity.href)} />
                        </Button>
                    </Tooltip>
                    <Tooltip message="참조">
                        <Button
                            type="button"
                            onClick={() => runEditorCommand(toolbarCommands.insertRef)}
                            disabled={!isTextDocument}
                        >
                            <Icon name={"crossRef"} size={"12px"} color={iconColor(buttonParity.ref)} />
                        </Button>
                    </Tooltip>
                    <Tooltip message="인용">
                        <Button
                            type="button"
                            onClick={() => runEditorCommand(toolbarCommands.insertCite)}
                            disabled={!isTextDocument}
                        >
                            <Icon name={"publisher"} size={"12px"} color={iconColor(buttonParity.cite)} />
                        </Button>
                    </Tooltip>
                    <ManuscriptTableDropdown
                        Button={Button}
                        disabled={!isTextDocument}
                        iconColor={iconColor(buttonParity.table)}
                        runEditorCommand={runEditorCommand}
                        open={activeToolbarDropdown === "table"}
                        onOpenChange={(nextOpen) => setActiveToolbarDropdown(nextOpen ? "table" : null)}
                        dropdownUsePortal={!isOverflowMenu}
                    />
                    <ManuscriptFigureDropdown
                        Button={Button}
                        disabled={!isTextDocument}
                        iconColor={iconColor(buttonParity.figure)}
                        onOpenFigureModal={onOpenInsertFigureModal}
                        runEditorCommand={runEditorCommand}
                        open={activeToolbarDropdown === "figure"}
                        onOpenChange={(nextOpen) => setActiveToolbarDropdown(nextOpen ? "figure" : null)}
                        dropdownUsePortal={!isOverflowMenu}
                    />
                </ToolbarButtonGroup>
            )}
            {showGroup("group-list") && (
                <ToolbarButtonGroup data-overflow="group-list" aria-label="목록">
                    <Tooltip message="글머리 기호">
                        <Button
                            type="button"
                            $active={activeFormatting.bulletList}
                            onClick={() => runEditorCommand(toolbarCommands.toggleBulletList)}
                            disabled={!isTextDocument}
                        >
                            <Icon name={"list"} size={"12px"} color={iconColor(buttonParity.bulletList)} />
                        </Button>
                    </Tooltip>
                    <Tooltip message="번호 매기기">
                        <Button
                            type="button"
                            $active={activeFormatting.numberedList}
                            onClick={() => runEditorCommand(toolbarCommands.toggleNumberedList)}
                            disabled={!isTextDocument}
                        >
                            <Icon name={"listNum"} size={"14px"} color={iconColor(buttonParity.numberedList)} />
                        </Button>
                    </Tooltip>
                </ToolbarButtonGroup>
            )}
            {showGroup("group-math") && (
                <ToolbarButtonGroup data-overflow="group-math" aria-label="수식 및 기호">
                    <ToolbarDivider />
                    <ManuscriptSymbolDropdown
                        Button={Button}
                        disabled={!isTextDocument}
                        iconColor={iconColor(buttonParity.symbol)}
                        runEditorCommand={runEditorCommand}
                        open={activeToolbarDropdown === "symbol"}
                        onOpenChange={(nextOpen) => setActiveToolbarDropdown(nextOpen ? "symbol" : null)}
                        dropdownUsePortal={!isOverflowMenu}
                    />
                    <Tooltip message="인라인 수식">
                        <Button
                            type="button"
                            onClick={() => runEditorCommand(toolbarCommands.wrapInInlineMath)}
                            disabled={!isTextDocument}
                            aria-label="인라인 수식 삽입"
                        >
                            <Icon name={"mathInline"} size={"14px"} color={iconColor(buttonParity.inlineMath)} />
                        </Button>
                    </Tooltip>
                    <Tooltip message="블록 수식">
                        <Button
                            type="button"
                            onClick={() => runEditorCommand(toolbarCommands.wrapInDisplayMath)}
                            disabled={!isTextDocument}
                            aria-label="블록 수식 삽입"
                        >
                            <Icon name={"mathBlock"} size={"14px"} color={iconColor(buttonParity.displayMath)} />
                        </Button>
                    </Tooltip>
                </ToolbarButtonGroup>
            )}
            {showGroup("group-search") && (
                <ToolbarButtonGroup data-overflow="group-search" aria-label="검색">
                    <ToolbarDivider />
                    <Tooltip message="검색">
                        <Button
                            type="button"
                            $active={activeFormatting.search}
                            onClick={() => runEditorCommand(toolbarCommands.toggleSearch)}
                            disabled={!selectedNode}
                        >
                            <Icon name={"search"} size={"11px"} color={iconColor(buttonParity.search)} />
                        </Button>
                    </Tooltip>
                </ToolbarButtonGroup>
            )}
        </>
    );
}

export default React.memo(ManuscriptEditorToolbar);
